import React, { useState, useEffect } from 'react';
import { supabase, extractErrorMessage, supabase_update_client } from '../services/supabaseService';
import { Building2, Save, Upload, MapPin, Receipt, CreditCard, ChevronLeft, Globe, Phone, Mail, Camera, CheckCircle2, Download, Trash2, Database } from 'lucide-react';
import { User } from '../types';

interface CompanySettingsProps {
  user: User;
  onBack: () => void;
}

const CompanySettings: React.FC<CompanySettingsProps> = ({ user, onBack }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    website: '',
    phone: '',
    email: '',
    logo_url: '',
    address_street: '',
    address_zip: '',
    address_city: '',
    invoice_kvk: '',
    invoice_vat: '',

    invoice_email: '',
    plan_tier: 'None',
    billing_status: 'None',
    active_site_editor: false,
    custom_domain: '',
    active_clientportal: false,
    clientportal_custom_domain: ''
  });

  useEffect(() => {
    loadClientData();
  }, []);

  const loadClientData = async () => {
    if (!user.clientId) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.from('clients').select('*').eq('id', user.clientId).single();
      if (error) throw error;
      if (data) {
        setFormData({
          name: data.name || '',
          website: data.website || '',
          phone: data.phone || '',
          email: data.email || '',
          logo_url: data.logo_url || '',
          address_street: data.address_street || '',
          address_zip: data.address_zip || '',
          address_city: data.address_city || '',
          invoice_kvk: data.invoice_kvk || '',
          invoice_vat: data.invoice_vat || '',
          invoice_email: data.invoice_email || '',
          plan_tier: data.plan_tier || 'Geen Actief Abonnement',
          billing_status: data.billing_status || 'Inactief',
          active_site_editor: data.active_site_editor || false,
          custom_domain: data.custom_domain || '',
          active_clientportal: data.active_clientportal || false,
          clientportal_custom_domain: data.clientportal_custom_domain || ''
        });
      }
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user.clientId) return;
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updates = {
        name: formData.name,
        website: formData.website,
        phone: formData.phone,
        email: formData.email,
        address_street: formData.address_street,
        address_zip: formData.address_zip,
        address_city: formData.address_city,
        invoice_kvk: formData.invoice_kvk,
        invoice_vat: formData.invoice_vat,
        invoice_email: formData.invoice_email
      };
      await supabase_update_client(user.clientId, updates);
      setSuccess('Bedrijfsgegevens succesvol opgeslagen.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user.clientId) return;
    setIsSaving(true);
    setError(null);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `client_${user.clientId}_${Math.random()}.${fileExt}`;
      const filePath = `public/logos/${fileName}`;
      const { error: uploadError } = await supabase.storage.from('public').upload(filePath, file, { upsert: true });
      if (uploadError) throw uploadError;
      const { data } = supabase.storage.from('public').getPublicUrl(filePath);
      
      const newLogoUrl = data.publicUrl;
      await supabase_update_client(user.clientId, { logo_url: newLogoUrl });
      
      setFormData(prev => ({ ...prev, logo_url: newLogoUrl }));
      setSuccess('Bedrijfslogo succesvol bijgewerkt.');
      setTimeout(() => setSuccess(null), 3000);
      
      // Dispatch event to allow App.tsx to reload the global branding (header logo)
      window.dispatchEvent(new CustomEvent('company-branding-updated'));
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleExportData = async () => {
    if (!user.clientId) return;
    setIsSaving(true);
    setError(null);
    try {
      // Fetch some sample data for export to provide a real JSON
      const { data: clientData } = await supabase.from('clients').select('*').eq('id', user.clientId).single();
      const { data: vacanciesData } = await supabase.from('vacancies').select('*').eq('client_id', user.clientId);
      const { data: candidatesData } = await supabase.from('candidates').select('*').eq('client_id', user.clientId);

      const exportPayload = {
        exportDate: new Date().toISOString(),
        client: clientData,
        vacancies: vacanciesData,
        candidates: candidatesData,
        info: "Volledige data export (tot max. 1000 records per tabel i.v.m. server limieten)."
      };

      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(exportPayload, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", `export_${formData.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(downloadAnchorNode); // required for firefox
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
      
      setSuccess('Data succesvol geëxporteerd.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(extractErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteData = () => {
    if (window.confirm("WEET U HET ZEKER? Dit verwijdert alle data en kan niet ongedaan worden gemaakt. Typ 'VERWIJDEREN' om door te gaan.")) {
        alert("Neem aub contact op met support@werfplaats.com om uw account en alle bijbehorende data definitief te laten verwijderen. Dit kan momenteel nog niet automatisch vanuit het dashboard om dataverlies te voorkomen.");
    }
  };

  if (isLoading) {
    return <div className="p-8 text-center text-slate-500 animate-pulse">Instellingen laden...</div>;
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div>
          <button onClick={onBack} className="mb-2 text-xs font-black text-slate-500 hover:text-slate-900 flex items-center gap-1.5 uppercase tracking-widest transition-colors mb-4">
             <ChevronLeft size={16} /> Terug naar Profiel
          </button>
          <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
            <Building2 className="text-red-500" />
            Bedrijfsinstellingen
          </h2>
          <p className="text-slate-500 text-sm mt-1">Beheer de gegevens, logo en facturatie van uw organisatie.</p>
        </div>
        <button 
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2 px-6 py-2.5 bg-[#e51b23] hover:bg-red-700 text-white rounded-lg font-bold transition-all shadow-sm hover:shadow-md disabled:opacity-50"
        >
          <Save size={18} />
          {isSaving ? 'Opslaan...' : 'Opslaan'}
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100 flex items-center gap-2 font-medium">
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-4 bg-emerald-50 text-emerald-700 rounded-lg text-sm border border-emerald-100 flex items-center gap-2 font-medium">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - General Info & Logo */}
        <div className="space-y-6 lg:col-span-2">
          
          {/* Logo Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 flex items-start gap-6">
             <div className="w-24 h-24 bg-slate-50 rounded-xl flex items-center justify-center overflow-hidden border border-slate-100 shrink-0 group relative cursor-pointer" title="Logo aanpassen">
                 {formData.logo_url ? (
                     <img src={formData.logo_url} className="w-full h-full object-contain p-2" alt={formData.name} />
                 ) : (
                     <Building2 className="text-slate-300" size={32} />
                 )}
                 <label className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer backdrop-blur-sm">
                     <Camera size={20} className="mb-1" />
                     <span className="text-[10px] font-bold tracking-wider uppercase">Upload</span>
                     <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={isSaving} />
                 </label>
             </div>
             <div>
                <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Bedrijfslogo</h3>
                <p className="text-xs text-slate-500 mt-1 mb-3">Upload een vierkant of transparant PNG/JPG logo (max 2MB). Dit logo wordt getoond op vacatures en portalen.</p>
                <div className="text-xs font-medium text-slate-400">Geadviseerd formaat: 400x400px</div>
             </div>
          </div>

          {/* General Details */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-100 flex items-center gap-2">
               <Building2 size={18} className="text-slate-400" />
               <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Algemene Bedrijfsgegevens</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Bedrijfsnaam</label>
                <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Globe size={14}/> Website</label>
                <input type="url" name="website" value={formData.website} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" placeholder="https://" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Phone size={14}/> Telefoon Algemeen</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5"><Mail size={14}/> Info Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
            <div className="p-6 border-b border-slate-100 flex items-center gap-2">
               <MapPin size={18} className="text-slate-400" />
               <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Bezoekadres</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Straat & Huisnummer</label>
                <input type="text" name="address_street" value={formData.address_street} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Postcode</label>
                <input type="text" name="address_zip" value={formData.address_zip} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Plaats</label>
                <input type="text" name="address_city" value={formData.address_city} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Financial & Subscription */}
        <div className="space-y-6">
          
          <div className="bg-white rounded-xl shadow-sm border border-slate-200">
             <div className="p-6 border-b border-slate-100 flex items-center gap-2">
               <Receipt size={18} className="text-slate-400" />
               <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Facturatie & Financieel</h3>
             </div>
             <div className="p-6 space-y-5">
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Facturatie Emailadres</label>
                  <input type="email" name="invoice_email" value={formData.invoice_email} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" placeholder="invoices@bedrijf.nl" />
               </div>
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">KVK Nummer</label>
                  <input type="text" name="invoice_kvk" value={formData.invoice_kvk} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono tracking-wide focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
               </div>
               <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">BTW Nummer (VAT)</label>
                  <input type="text" name="invoice_vat" value={formData.invoice_vat} onChange={handleChange} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-mono tracking-wide focus:bg-white focus:ring-2 focus:ring-red-100 focus:border-red-400 outline-none transition-all" />
               </div>
             </div>
          </div>

          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl shadow-sm border border-slate-700 text-white overflow-hidden relative">
             <div className="absolute right-0 top-0 w-32 h-32 bg-white/5 rounded-bl-full pointer-events-none"></div>
             <div className="p-6 border-b border-slate-700/50 flex items-center gap-2 relative z-10">
               <CreditCard size={18} className="text-slate-400" />
               <h3 className="text-sm font-bold text-white uppercase tracking-wide">Abonnement</h3>
             </div>
             <div className="p-6 relative z-10">
               <div className="text-3xl font-black mb-1">{formData.plan_tier || 'Geen Actief Abonnement'}</div>
               <div className="text-sm text-slate-400 font-medium mb-6">Huidige status: <span className={formData.billing_status === 'active' ? 'text-emerald-400' : 'text-amber-400'}>{formData.billing_status}</span></div>
               
               <a href="mailto:support@werfplaats.com?subject=Abonnement%20wijzigen" className="block w-full text-center py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm font-bold transition-colors">
                  Abonnement Wijzigen
               </a>
               <p className="text-[10px] text-slate-400 text-center mt-3 leading-relaxed">
                  Automatische incasso's worden maandelijks gefactureerd op het opgegeven facturatie emailadres.
               </p>
             </div>
          </div>

          {user.role !== 'CLIENT' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="p-6 border-b border-slate-100 flex items-center justify-between">
               <div className="flex items-center gap-2">
                 <Globe size={18} className="text-slate-400" />
                 <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Modules & Domeinen</h3>
               </div>
               <span className="text-[10px] uppercase font-black tracking-widest text-slate-400">Read Only</span>
             </div>
             
             <div className="divide-y divide-slate-100">
                {/* Werken-bij Website */}
                <div className="p-6 space-y-2">
                   <div className="flex items-center justify-between">
                     <span className="text-xs font-bold text-slate-700">Werken-bij Website</span>
                     {formData.active_site_editor ? (
                       <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><CheckCircle2 size={10}/> Actief</span>
                     ) : (
                       <span className="bg-slate-50 text-slate-500 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest">Inactief</span>
                     )}
                   </div>
                   {formData.active_site_editor && formData.custom_domain && (
                      <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Globe size={12}/> <a href={`https://${formData.custom_domain}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{formData.custom_domain}</a></p>
                   )}
                </div>

                {/* Clientportaal */}
                <div className="p-6 space-y-2">
                   <div className="flex items-center justify-between">
                     <span className="text-xs font-bold text-slate-700">Clientportaal</span>
                     {formData.active_clientportal ? (
                       <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest flex items-center gap-1"><CheckCircle2 size={10}/> Actief</span>
                     ) : (
                       <span className="bg-slate-50 text-slate-500 px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest">Inactief</span>
                     )}
                   </div>
                   {formData.active_clientportal && formData.clientportal_custom_domain && (
                      <p className="text-xs font-medium text-slate-500 flex items-center gap-1.5"><Globe size={12}/> <a href={`https://${formData.clientportal_custom_domain}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">{formData.clientportal_custom_domain}</a></p>
                   )}
                </div>
             </div>
             <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                <p className="text-[10px] font-medium text-slate-500 leading-relaxed text-center">Domeinen en modules worden beheerd door uw systeembeheerder. Neem contact op om dit te wijzigen.</p>
             </div>
          </div>
          )}

          {/* Data & Privacy Section */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
             <div className="p-6 border-b border-slate-100 flex items-center gap-2">
               <Database size={18} className="text-slate-400" />
               <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide">Data & Privacy</h3>
             </div>
             
             <div className="p-6 space-y-4">
                <button 
                  onClick={handleExportData}
                  disabled={isSaving}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg text-sm font-bold text-slate-700 transition-colors disabled:opacity-50"
                >
                  <span className="flex items-center gap-2"><Download size={16} className="text-slate-400" /> Exporteer JSON Data</span>
                  <span className="text-[10px] uppercase font-black tracking-widest text-slate-400">Export</span>
                </button>

                <button 
                  onClick={handleDeleteData}
                  disabled={isSaving}
                  className="w-full flex items-center justify-between px-4 py-3 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg text-sm font-bold text-red-700 transition-colors disabled:opacity-50"
                >
                  <span className="flex items-center gap-2"><Trash2 size={16} className="text-red-500" /> Verwijder Alle Data</span>
                  <span className="text-[10px] uppercase font-black tracking-widest text-red-400">Gevaar</span>
                </button>
             </div>
             <div className="bg-slate-50 px-6 py-4 border-t border-slate-100">
                <p className="text-[10px] font-medium text-slate-500 leading-relaxed text-center">Het verwijderen van een bedrijf en data is onomkeerbaar.</p>
             </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default CompanySettings;
